import type { Segment, OverlayConfig, MaskConfig, LogLevel, CropConfig } from './types';

export const MAX_SEGMENTS = 10;
export const SETTINGS_LABEL_W = 160;
export const SETTINGS_ROW_H = 32;
export const VOLUME_MIN = 0.5, VOLUME_MAX = 4.0;
export const REL_VOLUME_MIN = 0.25, REL_VOLUME_MAX = 2.5;

export const TL_SELECTED = '#f1c40f', TL_OTHER = '#3498db', TL_INTRO = '#6c757d';

export const LEVEL_RANK: Record<LogLevel, number> = { debug: 0, info: 1, warning: 2, error: 3 };
export const LEVEL_OPTIONS = [
  { value: 'debug', label: 'DEBUG' }, { value: 'info', label: 'INFO' },
  { value: 'warning', label: 'WARNING' }, { value: 'error', label: 'ERROR' },
];

export const isAudioFile = (p: string) => /\.(opus|wav|mp3|m4a|aac|flac|ogg|wma|mka)$/i.test(p);
export const isVideoFile = (p: string) => /\.(mp4|mov|mkv|webm|avi|flv|ts|m4v)$/i.test(p);
export const isImageFile = (p: string) => /\.(jpe?g|png|webp|gif)$/i.test(p);

let segSeq = 0;
export const newSegId = () => `${Date.now()}-${segSeq++}`;

export const formatClock = (d = new Date()) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

export function formatTime(s: number): string {
  if (!s || isNaN(s) || s < 0) s = 0;
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const p = (n: number) => String(n).padStart(2, '0'), intSec = Math.floor(sec), frac = Math.round((sec - intSec) * 100);
  return `${p(h)}:${p(m)}:${p(intSec)}${frac > 0 ? `.${p(frac)}` : ''}`;
}

export function parseTime(str: string): number {
  if (!str) return 0;
  const parts = str.split(':').map(Number);
  if (parts.some(isNaN)) return 0;
  return parts.length === 3 ? parts[0] * 3600 + parts[1] * 60 + parts[2] : (parts.length === 2 ? parts[0] * 60 + parts[1] : parts[0] || 0);
}

export function normalizeCrop(cr: any): CropConfig | null {
  if (!cr) return null;
  const [t, b, l, r] = ['top', 'bottom', 'left', 'right'].map(k => Math.max(0, Number(cr[k]) || 0));
  return (t || b || l || r) ? { top: t, bottom: b, left: l, right: r } : null;
}

export function normalizeOverlay(o: any): OverlayConfig {
  const c = o.overlay_coords || {};
  return {
    id: o.id || newSegId(), video: o.video || '', start: o.start || '', end: o.end || '',
    timeline_start: o.timeline_start || '', timeline_end: o.timeline_end || '',
    x: o.x ?? c.x ?? PIP_DEFAULT_X, y: o.y ?? c.y ?? PIP_DEFAULT_Y, w: o.w ?? c.w ?? PIP_DEFAULT_W,
    mix_audio: Boolean(o.mix_audio), color_enabled: Boolean(o.color_enabled),
    lift: Number(o.lift ?? c.lift) || 0, gamma: Number(o.gamma ?? c.gamma) || 1, gain: Number(o.gain ?? c.gain) || 1,
    crop: normalizeCrop(o.crop || o.overlay_crop),
  };
}

export function normalizeMask(m: any): MaskConfig {
  return {
    id: m.id || newSegId(),
    start: m.start || '00:00:00',
    end: m.end || '',
    image: m.image || '',
    color: m.color || '#000000',
    brushSize: Number(m.brushSize) || 24,
    name: m.name || '',
  };
}

export const getSegmentOverlays = (s?: Segment | null): OverlayConfig[] =>
  s ? (s.overlays && s.overlays.length > 0 ? s.overlays : (s.overlay?.video ? [s.overlay] : [])) : [];

export const getSegmentMasks = (s?: Segment | null): MaskConfig[] =>
  s?.masks && Array.isArray(s.masks) ? s.masks : [];

export type PipCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export const PIP_DEFAULT_HEIGHT = 0.3;
export const PIP_DEFAULT_X = 0.7, PIP_DEFAULT_Y = 0.7, PIP_DEFAULT_W = 0.3;

export const PIP_CORNERS: { corner: PipCorner; label: string }[] = [
  { corner: 'top-left', label: 'Вверх-Влево (впритык)' },
  { corner: 'top-right', label: 'Вверх-Вправо (впритык)' },
  { corner: 'bottom-left', label: 'Вниз-Влево (впритык)' },
  { corner: 'bottom-right', label: 'Вниз-Вправо (дефолт, впритык)' },
];

export function getPipSnapCoords(
  corner: PipCorner,
  w: number = 0.25,
  camWidth: number = 1920,
  camHeight: number = 1080,
  canvasWidth: number = 1920,
  canvasHeight: number = 1080,
  crop?: CropConfig | null
): { x: number; y: number; w: number } {
  const cw = canvasWidth || 1920, ch = canvasHeight || 1080, pw = camWidth || 1920, ph = camHeight || 1080;
  const cl = crop?.left || 0, cr = crop?.right || 0, ct = crop?.top || 0, cb = crop?.bottom || 0;
  const scaleX = w / pw, scaleY = scaleX * (cw / ch);
  let x: number, y: number;
  if (corner === 'top-left' || corner === 'bottom-left') x = -cl * scaleX;
  else x = 1.0 - (pw - cr) * scaleX;
  if (corner === 'top-left' || corner === 'top-right') y = -ct * scaleY;
  else y = 1.0 - (ph - cb) * scaleY;

  return { x: Math.round(x * 10000) / 10000, y: Math.round(y * 10000) / 10000, w: Math.round(w * 10000) / 10000 };
}

export function defaultPipCoords(camWidth = 1920, camHeight = 1080, canvasWidth = 1920, canvasHeight = 1080): { x: number; y: number; w: number } {
  const pw = camWidth || 1920, ph = camHeight || 1080, cw = canvasWidth || 1920, ch = canvasHeight || 1080;
  const w = PIP_DEFAULT_HEIGHT * ch * pw / (ph * cw);
  return getPipSnapCoords('bottom-right', w, pw, ph, cw, ch, null);
}

export function isOverlaySnapped(ov?: OverlayConfig | null, corner: PipCorner = 'bottom-right', camWidth = 1920, camHeight = 1080, canvasWidth = 1920, canvasHeight = 1080): boolean {
  if (!ov) return false;
  const snap = getPipSnapCoords(corner, ov.w ?? PIP_DEFAULT_W, camWidth, camHeight, canvasWidth, canvasHeight, ov.crop);
  return Math.abs(ov.x - snap.x) < 0.005 && Math.abs(ov.y - snap.y) < 0.005;
}

export const makeDefaultOverlay = (video = '', timelineStart = '', timelineEnd = ''): OverlayConfig => ({
  id: newSegId(), video, start: '', end: '', timeline_start: timelineStart, timeline_end: timelineEnd,
  x: PIP_DEFAULT_X, y: PIP_DEFAULT_Y, w: PIP_DEFAULT_W, mix_audio: false, color_enabled: false, lift: 0, gamma: 1, gain: 1, crop: null,
});

export const makeDefaultMask = (start = '00:00:00', end = '00:00:10', color = '#000000', brushSize = 24): MaskConfig => ({
  id: newSegId(), start, end, image: '', color, brushSize, name: '',
});

export const makeSegment = (video = ''): Segment => ({
  id: newSegId(), video, audio: video, start: '', end: '', fade_in: true, fade_out: true, overlay: null, overlays: [], volume: 1, crop: null, masks: [],
});

export function normalizeSegment(s: any): Segment {
  const overlays = (Array.isArray(s.overlays) ? s.overlays : (s.overlay?.video ? [s.overlay] : [])).map(normalizeOverlay);
  const masks = Array.isArray(s.masks) ? s.masks.map(normalizeMask) : [];
  return {
    id: s.id || newSegId(), video: s.video || '', audio: s.audio || s.video || '', start: s.start || '', end: s.end || '',
    fade_in: s.fade_in ?? true, fade_out: s.fade_out ?? true, overlay: overlays[0] || null, overlays, masks,
    volume: typeof s.volume === 'number' ? s.volume : 1, crop: normalizeCrop(s.crop),
  };
}


export function getSegmentOffsets(segId: string | null | undefined, segments: Segment[], displayBlocks: { segId: string | null; globalStart: number }[]): { globalStart: number; segStart: number } {
  if (!segId) return { globalStart: 0, segStart: 0 };
  const b = displayBlocks.find(x => x.segId === segId);
  const seg = segments.find(x => x.id === segId);
  const globalStart = b ? b.globalStart : 0;
  const segStart = seg ? parseTime(seg.start || '00:00:00') : 0;
  return { globalStart, segStart };
}
